import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout, Menu, Drawer } from 'antd';
import { pathToRegexp } from 'path-to-regexp';

import { authRoutes } from '../router';
import { LayoutContext } from '../contexts';
import { URLS } from '../constants';
import { useFetch } from '../hooks';

const { Sider } = Layout;

function SideBarLayout({ isMobile }) {
  const location = useLocation();
  const { pathname } = location;
  const [userSettings, setUserSettings] = useState(null);

  const isPathMatchRequestedUrl = path => !!pathToRegexp(path).exec(pathname);
  const { sideBarCollapsed, setCollapsed, theme, usernameComponent } = useContext(LayoutContext);

  const { doFetch: fetchUserSettings } = useFetch();

  useEffect(() => {
    // Fetch user settings to determine if Orders menu should be shown
    fetchUserSettings({
      url: URLS.userSettings,
      onSuccess: (data) => {
        setUserSettings(data);
      },
      onError: () => {
        // If we can't fetch settings, assume Orders should not be shown
        setUserSettings({
          business_api_url: null,
          is_showing_subcategories: true,
          is_showing_brands: true
        });
      },
    });
  }, [fetchUserSettings]);

  // Check if Orders menu should be shown (only if business_api_url is set)
  const shouldShowOrdersMenu = () => {
    return userSettings && userSettings.business_api_url;
  };

  // Check if Subcategories menu should be shown (based on display settings)
  const shouldShowSubcategoriesMenu = () => {
    return userSettings && userSettings.is_showing_subcategories !== false;
  };

  // Check if Brands menu should be shown (based on display settings)
  const shouldShowBrandsMenu = () => {
    return userSettings && userSettings.is_showing_brands !== false;
  };

  const getSiderStyle = () => {
    if (isMobile) {
      return {
        display: 'none' // Hide on mobile, will use drawer instead
      };
    }
    return {
      overflow: 'auto',
      height: '100vh',
      position: 'fixed',
      left: 0,
      zIndex: 100,
    };
  };

  const menuContent = (
    <>
      <div className="logo">
        <img src={require('../assets/img/favicon.png')} alt="" />
        {!sideBarCollapsed && <span> {usernameComponent}</span>}
      </div>
      <Menu theme={theme} mode="inline" activeKey={pathname} selectedKeys={[pathname]}>
        {authRoutes.map(({ path, showAlways, icon, name }) => {
          // Special handling for Orders menu - only show if business_api_url is set
          if (path === '/orders') {
            const showOrders = shouldShowOrdersMenu();
            const IconComponent = icon;
            if (showOrders || isPathMatchRequestedUrl(path)) {
              return (
                <Menu.Item key={isPathMatchRequestedUrl(path) ? pathname : path}>
                  <Link to={path} onClick={() => isMobile && setCollapsed(true)}>
                    {IconComponent && <IconComponent />}
                    <span className="nav-text">{name}</span>
                  </Link>
                </Menu.Item>
              );
            }
            return null;
          }

          // Special handling for Subcategories menu - show based on display settings
          if (path === '/subcategories') {
            const showSubcategories = shouldShowSubcategoriesMenu();
            const IconComponent = icon;
            if (showSubcategories || isPathMatchRequestedUrl(path)) {
              return (
                <Menu.Item key={isPathMatchRequestedUrl(path) ? pathname : path}>
                  <Link to={path} onClick={() => isMobile && setCollapsed(true)}>
                    {IconComponent && <IconComponent />}
                    <span className="nav-text">{name}</span>
                  </Link>
                </Menu.Item>
              );
            }
            return null;
          }

          // Special handling for Brands menu - show based on display settings
          if (path === '/brands') {
            const showBrands = shouldShowBrandsMenu();
            const IconComponent = icon;
            if (showBrands || isPathMatchRequestedUrl(path)) {
              return (
                <Menu.Item key={isPathMatchRequestedUrl(path) ? pathname : path}>
                  <Link to={path} onClick={() => isMobile && setCollapsed(true)}>
                    {IconComponent && <IconComponent />}
                    <span className="nav-text">{name}</span>
                  </Link>
                </Menu.Item>
              );
            }
            return null;
          }

          // Default behavior for other menu items
          const IconComponent = icon;
          if (showAlways || isPathMatchRequestedUrl(path)) {
            return (
              <Menu.Item key={isPathMatchRequestedUrl(path) ? pathname : path}>
                <Link to={path} onClick={() => isMobile && setCollapsed(true)}>
                  {IconComponent && <IconComponent />}
                  <span className="nav-text">{name}</span>
                </Link>
              </Menu.Item>
            );
          }
          return null;
        }).filter(Boolean)}
      </Menu>
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        title={null}
        placement="left"
        onClose={() => setCollapsed(true)}
        open={!sideBarCollapsed}
        bodyStyle={{
          padding: 0,
          background: '#001529',
          height: '100%'
        }}
        contentWrapperStyle={{
          background: '#001529'
        }}
        drawerStyle={{
          background: '#001529'
        }}
        headerStyle={{
          background: '#001529',
          border: 'none'
        }}
        width={250}
        className="mobile-sidebar-drawer"
        zIndex={1000}
      >
        <div style={{
          background: '#001529',
          height: '100%',
          width: '100%',
          overflow: 'hidden'
        }}>
          {menuContent}
        </div>
      </Drawer>
    );
  }

  return (
    <Sider
      collapsible
      collapsed={sideBarCollapsed}
      onCollapse={() => setCollapsed(!sideBarCollapsed)}
      width={200}
      style={getSiderStyle()}
      className="desktop-sidebar"
    >
      {menuContent}
    </Sider>
  );
}

export default SideBarLayout;
